import { mwLabel, nextRankFor } from '../game/scoring';
import type { TierRank } from '../game/types';
import type { GameSession } from '../game/session';
import { must, replayAnimation } from './dom';
import { str } from './strings';

/** Everything the win panel shows, resolved from game state — no DOM involved. */
export interface ResultView {
  title: string;
  /** The reference's topology name, stamped under the title on a perfect. */
  topology?: string;
  /** Chips, in order. Each may contain `<b>` emphasis. */
  stats: string[];
  /** Nudge under the chips; empty when there's nothing useful to say. */
  note: string;
  /** The capstone note gets the stamped-seal treatment (STYLEGUIDE §8). */
  engineers?: boolean;
  /**
   * The one line arguing for the share, already worded, or absent for
   * silence (STYLEGUIDE §7).
   */
  claim?: string;
  /**
   * Hold the claim's space while it is still empty: the flawless rarity can
   * land with the day's counters, and the space it fills must already be
   * there — never taken from under the road button. A card that can show no
   * claim holds nothing.
   */
  claimSlot?: boolean;
  /**
   * How the day's field did, at any size (GDD §4.7). Absent on a grid with
   * no race, on a field with nobody in it, and for a player who sends
   * nothing.
   */
  field?: string[];
}

export interface ResultInputs {
  /** MW of the layout currently on the board, or `null` if it's gone dark. */
  nowMW: number | null;
  /**
   * A move count an older version of the game recorded for this grid, or
   * `null` where there is none. The counter is retired (GDD §9); this is a
   * reading kept, never one taken.
   */
  retiredMoves?: number | null;
  /** Name of the reference's topology, or `null` to leave it unstamped. */
  topology?: string | null;
  /** The claim beside the share button, already worded, or `null` for silence. */
  claim?: string | null;
  /** Hold the claim's space while empty — a claim could still land. */
  claimSlot?: boolean;
  /** The field readout's lines, already rendered. */
  field?: string[] | null;
}

/** The distance still to close, as the note states it (GDD §6.2). */
function gapNote(mw: number, rank: TierRank): string {
  // Never null here: this is only reached on an unmatched grid, and `tierFor`
  // and `matchedReference` read the same cut (GDD §3.5).
  const next = nextRankFor(rank) ?? { rank, at: mw };
  // Rounded before labelling so a float's 4.999 prints as 5, not 5.0.
  const gap = Math.round((next.at - mw) * 10) / 10;
  return str().noteGapTo(mwLabel(gap), str().ranks[next.rank]);
}

/**
 * The panel is deliberately not a "you won, stop playing" screen — it keeps
 * showing the current layout against the player's best so tuning stays live.
 */
export function buildResultView(session: GameSession, inputs: ResultInputs): ResultView {
  const best = session.best;
  const tier = session.tier();
  if (!best || !tier) throw new Error('buildResultView() before the grid was ever completed');

  const { nowMW, retiredMoves, topology, claim, claimSlot, field } = inputs;
  const matched = session.matchedReference;
  // A drill's card marks a different event: the grid was energized before the
  // player touched it, and what the panel announces is the naive line beaten
  // (GDD §4.6). Its numbers compare against that line, not a cold read.
  const naiveMW = session.naiveLineMW;
  const naiveWire = session.naiveLineWire;
  const drill = session.mode === 'drill' && naiveMW != null && naiveWire != null;

  // Chips are stat columns (STYLEGUIDE §7): dim uppercase label (<i>) over a
  // mono value, the value bits grouped in a `.v` row so a unit sits beside
  // its number. The tier keeps its emoji for the share; here it is a rank in
  // plain text, in the same mono as its peers — type is the whole treatment.
  //
  // The card's readings are best, first take, rank — and only those: the
  // live output, the tower count and the streak already read in the strip
  // above the board, and a card that repeats the HUD is a longer card, not
  // a fuller one. A retired move count may follow.
  // The best states its denominator (GDD §3.4). The 100 is the reference's by
  // definition, but that is taught by the legend and the legend retires — a
  // bare number reads as a verdict to the player who has not learned it yet.
  // Only this column carries it, so the scale is stated once.
  const stats: string[] = [
    `<i>${str().chipBest}</i> <span class="v"><b class="mw">${mwLabel(best.mw)}<span class="of">/100</span></b> <i>${str().unitMW}</i></span>`,
  ];
  if (drill) {
    // The naive line stands in for the first take: the drill's opening
    // reading is the drill's, not the player's. Every MW reading states its
    // unit — a unit on one number and not its neighbour reads as a slip.
    stats.push(
      `<i>${str().chipNaive}</i> <span class="v"><b>${mwLabel(naiveMW)}</b> <i>${str().unitMW}</i></span>`,
    );
  } else if (session.firstMW != null && mwLabel(session.firstMW) !== mwLabel(best.mw)) {
    // Only when it differs (GDD §6.2). Compared as the labels the player
    // reads, not as the raw numbers: a cold read the card would print as the
    // best beside the best is a column that says nothing.
    stats.push(
      `<i>${str().firstTake}</i> <span class="v"><b>${mwLabel(session.firstMW)}</b> <i>${str().unitMW}</i></span>`,
    );
  }
  // No target chip: the target is 100 every day (GDD §3.4), and a constant is
  // not information. The legend names the 100's owner instead.
  stats.push(`<i>${str().chipRank}</i> <span class="v"><b>${str().ranks[tier.rank]}</b></span>`);
  // Dimmed and unadorned: a fact from a version of the game that counted
  // moves, not an award this one still hands out (STYLEGUIDE §7).
  if (retiredMoves != null) {
    stats.push(
      `<span class="retired"><i>${str().chipMoves}</i> <span class="v"><b>${retiredMoves}</b></span></span>`,
    );
  }

  const engineers = matched && session.beatReference;
  // One note, and the biggest one wins: beating the engineers outranks the
  // flawless callout, which outranks having nothing left to say. On a drill
  // the note restates the lesson as wire saved against the naive line; the
  // flawless callout never applies there, because the board opens at par and
  // the first move is always a removal.
  const savedPct = drill ? Math.round((1 - best.wire / naiveWire) * 100) : null;
  const note =
    savedPct != null
      ? engineers
        ? str().noteBeatEngineers
        : matched
          ? str().noteLessWire(savedPct)
          : nowMW == null
            ? str().noteGridDark
            : str().noteLessWireTuning(savedPct)
      : matched
        ? session.beatReference
          ? str().noteBeatEngineers
          : session.flawless
            ? str().noteFlawless
            : ''
        : nowMW == null
          ? str().noteGridDark
          : gapNote(best.mw, tier.rank);

  return {
    // The head counts takes rather than announcing a finished state: a number
    // that climbs as the player works is the card's own evidence the grid is
    // unfinished, which no note underneath a verdict can undo (GDD §6.2).
    // The count is the trail's length — readings that bettered the last one,
    // the same number telemetry sends as `bestCount` — so the head and the
    // tuning rate never disagree.
    title: matched
      ? str().perfectGrid
      : drill
        ? str().lineBeaten
        : str().takeN(Math.max(1, session.mwTrail.length)),
    ...(matched && topology ? { topology } : {}),
    stats,
    note,
    engineers,
    ...(claim ? { claim } : {}),
    ...(claimSlot ? { claimSlot: true } : {}),
    ...(field?.length ? { field } : {}),
  };
}

/** Gap between chips as the card counts itself out, in ms. */
export const BEAT_GAP = 110;

export interface ShowOptions {
  /**
   * Reveal the chips one at a time instead of all at once. Reserved for
   * moments worth marking — the first solve, and the perfect grid — so that
   * routine re-tuning doesn't replay the fanfare on every tap.
   */
  stage?: boolean;
  /** Called once per chip as it lands, for the accompanying beat. */
  onBeat?: (index: number, total: number) => void;
}

/** Renders {@link ResultView}, flashing only when something actually changed. */
export class ResultPanel {
  private lastHTML = '';
  private beats: ReturnType<typeof setTimeout>[] = [];

  show(view: ResultView, opts: ShowOptions = {}): void {
    const panel = must('winPanel');
    panel.classList.add('show');
    // Publish the cadence to the stylesheet, so the CSS reveal and the JS
    // beats share one clock instead of two hardcoded 110s.
    panel.style.setProperty('--beat-gap', `${BEAT_GAP}ms`);
    must('winTitle').textContent = view.title;

    // The topology name is a stamp on the title, not a chip: it names the
    // shape the reference turned out to be, which is a fact about the day
    // rather than a reading off the player's board (GDD §3.5).
    const topo = must('winTopology');
    topo.textContent = view.topology ?? '';
    topo.hidden = !view.topology;

    // The claim, or the space held open for one still landing. A card with
    // neither shows no slot at all, so the road button sits exactly where a
    // claimless card always put it (STYLEGUIDE §7).
    const slot = must('winClaim');
    slot.textContent = view.claim ?? '';
    slot.hidden = !view.claim && !view.claimSlot;

    const chips = view.stats
      .map((s, i) => `<span class="stat" style="--beat:${i}">${s}</span>`)
      .join('');
    // Everything below the chips keeps counting on from where they stopped, so
    // the staged reveal stays one cadence rather than restarting per block.
    let beat = view.stats.length;
    const html =
      `<span class="win-stats">${chips}</span>` +
      (view.note
        ? `<span class="win-note${view.engineers ? ' engineers' : ''}" style="--beat:${beat++}">${view.note}</span>`
        : '');

    // The field renders into its own block *below* the action row. It is still
    // under the player's own numbers, which is what GDD §4.7 asks — but it
    // arrives asynchronously, and inside this block it would push `Copy
    // result` off a short screen moments after the player had seen it.
    const fieldHTML = (view.field ?? [])
      .map((line) => `<span class="win-field" style="--beat:${beat++}">${line}</span>`)
      .join('');

    const claimKey = (view.claim ?? '') + (view.claimSlot ? '+slot' : '');
    if (html + fieldHTML + (view.topology ?? '') + claimKey === this.lastHTML && !opts.stage)
      return;

    const text = must('winText');
    const fieldBlock = must('winField');
    this.clearBeats();
    text.innerHTML = html;
    fieldBlock.innerHTML = fieldHTML;
    fieldBlock.hidden = !fieldHTML;
    text.classList.toggle('staged', Boolean(opts.stage));
    fieldBlock.classList.toggle('staged', Boolean(opts.stage));

    if (opts.stage) {
      replayAnimation(must('winTitle'), 'stamp');
      replayAnimation(must('winPanel'), 'celebrate');
      this.scheduleBeats(view, opts.onBeat);
    } else {
      replayAnimation(text, 'tick');
    }

    this.lastHTML = html + fieldHTML + (view.topology ?? '') + claimKey;
  }

  /** Fire one beat per chip, in step with the CSS reveal. */
  private scheduleBeats(view: ResultView, onBeat?: ShowOptions['onBeat']): void {
    if (!onBeat) return;
    const total = view.stats.length;
    for (let i = 0; i < total; i++) {
      this.beats.push(setTimeout(() => onBeat(i, total), i * BEAT_GAP));
    }
  }

  private clearBeats(): void {
    for (const t of this.beats) clearTimeout(t);
    this.beats = [];
  }

  hide(): void {
    this.clearBeats();
    must('winPanel').classList.remove('show');
    const fieldBlock = must('winField');
    fieldBlock.innerHTML = '';
    fieldBlock.hidden = true;
    const slot = must('winClaim');
    slot.textContent = '';
    slot.hidden = true;
    this.lastHTML = '';
  }

  /** Bring the panel into view after the surge lands. */
  scrollIntoView(smooth: boolean): void {
    must('winPanel').scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest' });
  }
}
